import { html, nothing, type TemplateResult } from "lit";
import "../components/metric-tile.ts";
import "../components/sparkline.ts";
import type { GatewayHelloOk } from "../gateway.ts";
import type { UiSettings } from "../storage.ts";
import { formatRelativeTimestamp, formatDurationHuman } from "../format.ts";
import { formatNextRun } from "../presenter.ts";
import {
  type OverviewDataState,
  type AgentUsageEntry,
  type ProviderBreakdownEntry,
  type ProviderPlanEntry,
  type DailyTokenEntry,
  type DailyCostEntry,
  type TimeRange,
  TIME_RANGE_OPTIONS,
} from "../controllers/overview.ts";
import type { PresenceEntry } from "../types.ts";
import { formatPresenceAge, formatPresenceSummary } from "../presenter.ts";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type IgloobotStats = {
  projects: number;
  users: number;
  tasks: number;
  messages: number;
  sessions: number;
  avatarSessions: number;
  skills: number;
  n8nWorkflows: number;
  cronJobs: number;
  documents: number;
  emailConfigured: boolean;
};

export type OverviewProps = OverviewDataState & {
  hello: GatewayHelloOk | null;
  settings: UiSettings;
  password: string;
  lastError: string | null;
  presenceCount: number;
  sessionsCount: number | null;
  cronEnabled: boolean | null;
  cronNext: number | null;
  lastChannelsRefresh: number | null;
  igStats: IgloobotStats | null;
  igStatsAvailable: boolean;
  onSettingsChange: (next: UiSettings) => void;
  onPasswordChange: (next: string) => void;
  onSessionKeyChange: (next: string) => void;
  onConnect: () => void;
  onRefresh: () => void;
  onTimeRangeChange: (range: TimeRange) => void;
  // Connected instances (moved from Instances tab)
  presenceEntries: PresenceEntry[];
  presenceLoading: boolean;
  presenceError: string | null;
  presenceStatus: string | null;
  onRefreshPresence: () => void;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

function formatCost(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  // Costs are ~1e6 smaller after the per-million fix; don't collapse a real
  // sub-cent spend to "$0".
  if (n >= 0.000001) return `$${n.toFixed(6)}`;
  if (n > 0) return "<$0.000001";
  return "$0";
}

function formatTrend(pct: number): string {
  if (Number.isNaN(pct) || pct === 0) return "";
  const sign = pct > 0 ? "↑" : "↓";
  return `${sign}${Math.abs(pct)}%`;
}

function trendColor(pct: number): string {
  if (pct > 0) return "var(--warn)";
  if (pct < 0) return "var(--ok)";
  return "var(--muted)";
}

function agentStatusClass(lastActive: number | null): string {
  if (!lastActive) return "agent-gray";
  const ageMin = (Date.now() - lastActive) / 60_000;
  if (ageMin < 5) return "agent-green";
  if (ageMin < 60) return "agent-amber";
  return "agent-gray";
}

function planBarColor(pct: number): string {
  if (pct >= 90) return "var(--danger)";
  if (pct >= 70) return "var(--warn)";
  return "var(--ok)";
}

// ---------------------------------------------------------------------------
// Section 1: Consumption Dashboard
// ---------------------------------------------------------------------------

function renderHeroCard(
  label: string,
  value: string,
  sub: string,
  trend?: string,
  trendColorVal?: string,
): TemplateResult {
  return html`
    <div class="hero-card" style="
      flex:1;min-width:140px;padding:14px 16px;border-radius:8px;
      background:var(--card);border:1px solid var(--glass-border);
    ">
      <div style="font-size:0.75em;color:var(--muted);text-transform:uppercase;letter-spacing:0.05em;">
        ${label}
      </div>
      <div style="font-size:1.6em;font-weight:700;margin:4px 0;font-variant-numeric:tabular-nums;">
        ${value}
        ${trend
          ? html`<span style="font-size:0.7em;font-weight:400;color:${trendColorVal ?? "var(--muted)"};margin-left:6px;">${trend}</span>`
          : nothing}
      </div>
      <div style="font-size:0.75em;color:var(--muted);">${sub}</div>
    </div>
  `;
}

function renderConsumptionDashboard(props: OverviewProps): TemplateResult | typeof nothing {
  const d = props.dailyTokens ?? [];
  const dc = props.dailyCost ?? [];
  const sparkData = d.slice(-7).map((entry) => ({
    label: entry.date.slice(5),
    value: entry.input,
    secondary: entry.output,
    tertiary: entry.cacheRead,
  }));
  const costData = dc.slice(-7).map((entry) => ({
    label: entry.date.slice(5),
    value: Math.round(entry.cost * 1000),
  }));

  return html`
    <div class="ops-eyebrow">Token Consumption</div>

    <!-- Hero metrics -->
    <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:16px;">
      ${renderHeroCard(
        `Tokens ${props.overviewChartLabel}`,
        formatTokens(props.tokensToday),
        `in: ${formatTokens(props.tokensInput)} · out: ${formatTokens(props.tokensOutput)}`,
        formatTrend(props.tokensTrend),
        trendColor(props.tokensTrend),
      )}
      ${renderHeroCard(
        `Cost ${props.overviewChartLabel}`,
        formatCost(props.costToday),
        `${props.overviewChartLabel}: ${formatCost(dc.reduce((s, e) => s + e.cost, 0))}`,
        formatTrend(props.costTrend),
        trendColor(props.costTrend),
      )}
      ${renderHeroCard(
        "Cache Hit Rate",
        `${Math.round(props.cacheHitRate * 100)}%`,
        `${formatTokens(d.reduce((s, e) => s + (e.cacheRead ?? 0), 0))} cached`,
      )}
      ${renderHeroCard(
        "Active Sessions",
        String(props.activeSessions),
        `${props.systemStats?.sessions ?? 0} total sessions`,
      )}
    </div>

    <!-- Sparkline charts -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;">
      <div class="card" style="padding:14px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <strong style="font-size:0.85em;">Tokens (${props.overviewChartLabel})</strong>
          <div style="display:flex;gap:8px;font-size:0.7em;">
            <span style="color:var(--accent);">■ Input</span>
            <span style="color:var(--info);">■ Output</span>
            <span style="color:var(--warn);">■ Cache</span>
          </div>
        </div>
        <ig-sparkline
          .data=${sparkData}
          .height=${70}
          .color="var(--accent)"
          .secondaryColor="var(--info)"
          .tertiaryColor="var(--warn)"
        ></ig-sparkline>
      </div>
      <div class="card" style="padding:14px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <strong style="font-size:0.85em;">Cost (${props.overviewChartLabel})</strong>
          <span style="font-size:0.7em;color:var(--muted);">in milli-dollars</span>
        </div>
        <ig-sparkline
          .data=${costData}
          .height=${70}
          .color="var(--warn)"
        ></ig-sparkline>
      </div>
    </div>

    <!-- Provider breakdown table -->
    ${props.byProvider.length > 0
      ? html`
          <div class="card" style="padding:14px;margin-bottom:16px;">
            <strong style="font-size:0.85em;">Provider Breakdown (${props.overviewChartLabel})</strong>
            <table style="width:100%;margin-top:8px;font-size:0.8em;border-collapse:collapse;">
              <thead>
                <tr style="text-align:left;color:var(--muted);border-bottom:1px solid var(--glass-border);">
                  <th style="padding:4px 8px;">Provider</th>
                  <th style="padding:4px 8px;">Model</th>
                  <th style="padding:4px 8px;text-align:right;">Tokens</th>
                  <th style="padding:4px 8px;text-align:right;">Cost</th>
                  <th style="padding:4px 8px;text-align:right;">%</th>
                </tr>
              </thead>
              <tbody>
                ${props.byProvider.map((p) => html`
                  <tr style="border-bottom:1px solid var(--border);">
                    <td style="padding:4px 8px;">${p.provider}</td>
                    <td style="padding:4px 8px;color:var(--muted);">${p.model ?? "—"}</td>
                    <td style="padding:4px 8px;text-align:right;font-variant-numeric:tabular-nums;">${formatTokens(p.tokens)}</td>
                    <td style="padding:4px 8px;text-align:right;font-variant-numeric:tabular-nums;">${formatCost(p.cost)}</td>
                    <td style="padding:4px 8px;text-align:right;">
                      <div style="display:flex;align-items:center;gap:4px;justify-content:flex-end;">
                        <div style="width:40px;height:6px;border-radius:3px;background:var(--bg-muted);overflow:hidden;">
                          <div style="width:${p.percent}%;height:100%;background:var(--accent);"></div>
                        </div>
                        <span style="min-width:30px;text-align:right;">${p.percent}%</span>
                      </div>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        `
      : nothing}
  `;
}

// ---------------------------------------------------------------------------
// Section 2: Per-Agent Cards
// ---------------------------------------------------------------------------

function renderAgentCard(agent: AgentUsageEntry): TemplateResult {
  const statusClass = agentStatusClass(agent.lastActive);
  const lastActiveText = agent.lastActive
    ? formatRelativeTimestamp(agent.lastActive)
    : "unknown";

  return html`
    <div class="agent-card ${statusClass}" style="
      padding:12px 14px;border-radius:8px;
      background:var(--card);border:1px solid var(--glass-border);
      border-left:3px solid var(--accent);
    ">
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <div>
          <strong>${agent.name ?? agent.id}</strong>
          ${agent.model
            ? html`<span style="font-size:0.7em;color:var(--muted);margin-left:6px;">${agent.model}</span>`
            : nothing}
        </div>
        <span class="agent-status-dot ${statusClass}" style="
          width:8px;height:8px;border-radius:50%;
          background:${statusClass === "agent-green" ? "var(--ok)" : statusClass === "agent-amber" ? "var(--warn)" : "var(--muted)"};
        "></span>
      </div>
      ${agent.fallbacks?.length
        ? html`<div style="margin:4px 0;font-size:0.7em;">
            ${agent.fallbacks.map((f) => html`<span class="pill pill--sm" style="margin-right:4px;">${f}</span>`)}
          </div>`
        : nothing}
      <div style="display:flex;gap:16px;margin-top:8px;font-size:0.8em;">
        <div>
          <span style="color:var(--muted);">Tokens</span>
          <strong style="margin-left:4px;">${formatTokens(agent.tokensToday)}</strong>
        </div>
        <div>
          <span style="color:var(--muted);">Cost</span>
          <strong style="margin-left:4px;">${formatCost(agent.costToday)}</strong>
        </div>
      </div>
      <div style="font-size:0.7em;color:var(--muted);margin-top:6px;">
        Active ${lastActiveText}
      </div>
    </div>
  `;
}

function renderAgentCards(props: OverviewProps): TemplateResult | typeof nothing {
  if (!props.agentUsage.length) return nothing;

  return html`
    <div class="ops-eyebrow">Agent Usage (${props.overviewChartLabel})</div>
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:10px;margin-bottom:16px;">
      ${props.agentUsage.map((agent) => renderAgentCard(agent))}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Section 3: Provider Plans + System Health
// ---------------------------------------------------------------------------

function renderProviderPlans(props: OverviewProps): TemplateResult | typeof nothing {
  if (!props.providerPlans.length) return nothing;

  return html`
    <div class="card" style="padding:14px;">
      <div class="card-title" style="font-size:0.85em;">Provider Plans</div>
      <div style="margin-top:10px;">
        ${props.providerPlans.map((p) => {
          const window = p.windows[0];
          if (!window) return nothing;
          const pct = Math.round(window.usedPercent);
          const color = planBarColor(pct);
          return html`
            <div style="margin-bottom:10px;">
              <div style="display:flex;justify-content:space-between;align-items:center;font-size:0.8em;">
                <strong>${p.displayName}</strong>
                <span style="color:var(--muted);">${p.plan ?? ""}</span>
              </div>
              <div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
                <div style="flex:1;height:8px;border-radius:4px;background:var(--bg-muted);overflow:hidden;">
                  <div style="width:${pct}%;height:100%;background:${color};border-radius:4px;transition:width 0.3s;"></div>
                </div>
                <span style="font-size:0.75em;min-width:36px;text-align:right;font-variant-numeric:tabular-nums;">${pct}%</span>
              </div>
              <div style="font-size:0.7em;color:var(--muted);margin-top:2px;">
                ${window.label}
                ${window.resetAt ? ` · resets ${formatRelativeTimestamp(window.resetAt)}` : ""}
                ${p.error ? ` · ${p.error}` : ""}
              </div>
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

function renderSystemHealth(props: OverviewProps): TemplateResult {
  const snapshot = props.hello?.snapshot as
    | { uptimeMs?: number; policy?: { tickIntervalMs?: number } }
    | undefined;
  const uptime = snapshot?.uptimeMs ? formatDurationHuman(snapshot.uptimeMs) : "n/a";
  const s = props.igStats;

  type HealthCard = {
    label: string;
    value: string;
    sub: string;
    ok: boolean;
    icon: string;
  };

  const cards: HealthCard[] = [
    {
      label: "Gateway",
      value: props.connected ? "Online" : "Offline",
      sub: `Up ${uptime}`,
      ok: props.connected,
      icon: "●",
    },
    {
      label: "WebSocket",
      value: props.gatewayConnected ? "Connected" : "Down",
      sub: props.gatewayConnected ? "Live link" : "No link",
      ok: props.gatewayConnected,
      icon: "⇄",
    },
    {
      label: "Cron",
      value: props.cronEnabled == null ? "N/A" : props.cronEnabled ? "Running" : "Stopped",
      sub: props.cronNext ? `Next: ${formatNextRun(props.cronNext)}` : "No scheduled jobs",
      ok: props.cronEnabled ?? false,
      icon: "⏱",
    },
    {
      label: "Channels",
      value: props.lastChannelsRefresh ? "Synced" : "Pending",
      sub: props.lastChannelsRefresh
        ? formatRelativeTimestamp(props.lastChannelsRefresh)
        : "Not synced yet",
      ok: !!props.lastChannelsRefresh,
      icon: "📡",
    },
  ];

  if (s) {
    cards.push(
      {
        label: "Email",
        value: s.emailConfigured ? "Ready" : "Off",
        sub: s.emailConfigured ? "SMTP + IMAP" : "Not configured",
        ok: s.emailConfigured,
        icon: "✉",
      },
      {
        label: "Avatar",
        value: s.avatarSessions > 0 ? `${s.avatarSessions} active` : "Idle",
        sub: s.avatarSessions > 0 ? "Video sessions" : "No video calls",
        ok: s.avatarSessions > 0,
        icon: "🎥",
      },
    );
  }

  const platformStats: Array<{ label: string; value: number }> = s
    ? [
        { label: "Projects", value: s.projects },
        { label: "Users", value: s.users },
        { label: "Tasks", value: s.tasks },
        { label: "Messages", value: s.messages },
        { label: "Skills", value: s.skills },
        { label: "Documents", value: s.documents },
        { label: "n8n Flows", value: s.n8nWorkflows },
        { label: "Cron Jobs", value: s.cronJobs },
      ]
    : [];

  return html`
    <div class="card" style="padding:16px;">
      <div class="card-title" style="font-size:0.85em;">System Health</div>

      <!-- Health cards grid -->
      <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin-top:12px;">
        ${cards.map((c) => html`
          <div style="
            padding:10px 12px;border-radius:8px;
            background:var(--card);
            border:1px solid var(--glass-border);
            border-left:3px solid ${c.ok ? "var(--ok)" : "var(--warn)"};
            display:flex;align-items:center;gap:10px;
          ">
            <span style="
              font-size:1.2em;
              color:${c.ok ? "var(--ok)" : "var(--warn)"};
              line-height:1;
            ">${c.icon}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-size:0.7em;color:var(--muted);text-transform:uppercase;letter-spacing:0.04em;">
                ${c.label}
              </div>
              <div style="font-size:0.85em;font-weight:600;">
                ${c.value}
              </div>
              <div style="font-size:0.7em;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
                ${c.sub}
              </div>
            </div>
          </div>
        `)}
      </div>

      <!-- Platform stats grid -->
      ${platformStats.length > 0
        ? html`
            <div style="margin-top:12px;padding-top:10px;border-top:1px solid var(--glass-border);">
              <div style="font-size:0.7em;color:var(--muted);text-transform:uppercase;letter-spacing:0.04em;margin-bottom:8px;">
                Platform
              </div>
              <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;">
                ${platformStats.map((p) => html`
                  <div style="
                    text-align:center;padding:6px 4px;
                    border-radius:6px;
                    background:var(--bg-muted);
                  ">
                    <div style="font-size:1.1em;font-weight:700;font-variant-numeric:tabular-nums;">
                      ${p.value}
                    </div>
                    <div style="font-size:0.65em;color:var(--muted);">
                      ${p.label}
                    </div>
                  </div>
                `)}
              </div>
            </div>
          `
        : nothing}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Section 4: Collapsible Gateway Access + Maintenance
// ---------------------------------------------------------------------------

function renderGatewayAccess(props: OverviewProps): TemplateResult {
  return html`
    <details style="margin-top:8px;">
      <summary style="
        cursor:pointer;font-size:0.85em;font-weight:600;
        padding:8px 12px;border-radius:6px;
        background:var(--card);border:1px solid var(--glass-border);
        list-style:none;display:flex;align-items:center;justify-content:space-between;
      ">
        <span>Gateway Access & Maintenance</span>
        <span style="font-size:0.7em;color:var(--muted);">click to expand</span>
      </summary>
      <div style="padding:14px;margin-top:8px;border-radius:6px;background:var(--card);border:1px solid var(--glass-border);">
        <div class="form-grid" style="margin-top:8px;">
          <label class="field">
            <span>WebSocket URL</span>
            <input
              .value=${props.settings.gatewayUrl}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSettingsChange({ ...props.settings, gatewayUrl: v });
              }}
              placeholder="ws://100.x.y.z:18789"
            />
          </label>
          <label class="field">
            <span>Gateway Token</span>
            <input
              .value=${props.settings.token}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSettingsChange({ ...props.settings, token: v });
              }}
              placeholder="IGLOOBOT_GATEWAY_TOKEN"
            />
          </label>
          <label class="field">
            <span>Password (not stored)</span>
            <input
              type="password"
              .value=${props.password}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onPasswordChange(v);
              }}
              placeholder="system or shared password"
            />
          </label>
          <label class="field">
            <span>Default Session Key</span>
            <input
              .value=${props.settings.sessionKey}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSessionKeyChange(v);
              }}
            />
          </label>
        </div>
        <div class="row" style="margin-top: 12px; gap: 10px;">
          <button class="btn" @click=${() => props.onConnect()}>Connect</button>
          <button
            class="btn"
            @click=${async () => {
              try {
                const url = new URL(props.settings.gatewayUrl);
                url.protocol = url.protocol.replace("ws", "http");
                url.pathname = "/api/backup";
                const res = await fetch(url.toString(), {
                  headers: { Authorization: `Bearer ${props.settings.token}` },
                });
                if (!res.ok) throw new Error(`Backup failed (HTTP ${res.status})`);
                const blob = await res.blob();
                const href = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = href;
                a.download = "igloobot-backup.tar.gz";
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(href);
              } catch (err) {
                alert(`Could not download backup: ${(err as Error).message}`);
              }
            }}
          >
            Download backup
          </button>
        </div>
      </div>
    </details>
  `;
}

// ---------------------------------------------------------------------------
// Section: Connected Instances (moved from Instances tab)
// ---------------------------------------------------------------------------

function renderConnectedInstances(props: OverviewProps): TemplateResult | typeof nothing {
  return html`
    <div class="ops-eyebrow">Connected Instances</div>
    <div class="card" style="padding:16px;margin-bottom:16px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
        <div>
          <div class="card-title" style="font-size:0.85em;">Presence Beacons</div>
          <div style="font-size:0.75em;color:var(--muted);">Connected clients and nodes</div>
        </div>
        <button class="btn btn--sm" ?disabled=${props.presenceLoading} @click=${props.onRefreshPresence}>
          ${props.presenceLoading ? "Loading…" : "Refresh"}
        </button>
      </div>
      ${props.presenceError
        ? html`<div class="callout danger" style="margin:8px 0;">${props.presenceError}</div>`
        : nothing}
      ${props.presenceStatus
        ? html`<div class="callout" style="margin:8px 0;">${props.presenceStatus}</div>`
        : nothing}
      <div class="list">
        ${props.presenceEntries.length === 0
          ? html`<div class="muted" style="padding:12px;">No instances reported yet.</div>`
          : props.presenceEntries.map((entry) => {
              const lastInput = entry.lastInputSeconds != null ? `${entry.lastInputSeconds}s ago` : "n/a";
              const mode = entry.mode ?? "unknown";
              const roles = Array.isArray(entry.roles) ? entry.roles.filter(Boolean) : [];
              const scopes = Array.isArray(entry.scopes) ? entry.scopes.filter(Boolean) : [];
              const scopesLabel = scopes.length > 0
                ? scopes.length > 3 ? `${scopes.length} scopes` : `scopes: ${scopes.join(", ")}`
                : null;
              return html`
                <div class="list-item" style="padding:10px 0;border-bottom:1px solid var(--glass-border);">
                  <div class="list-main">
                    <div class="list-title" style="font-size:0.85em;">${entry.host ?? "unknown host"}</div>
                    <div class="list-sub" style="font-size:0.75em;">${formatPresenceSummary(entry)}</div>
                    <div class="chip-row" style="margin-top:4px;">
                      <span class="chip" style="font-size:0.7em;">${mode}</span>
                      ${roles.map((role) => html`<span class="chip" style="font-size:0.7em;">${role}</span>`)}
                      ${scopesLabel ? html`<span class="chip" style="font-size:0.7em;">${scopesLabel}</span>` : nothing}
                      ${entry.platform ? html`<span class="chip" style="font-size:0.7em;">${entry.platform}</span>` : nothing}
                      ${entry.deviceFamily ? html`<span class="chip" style="font-size:0.7em;">${entry.deviceFamily}</span>` : nothing}
                      ${entry.modelIdentifier ? html`<span class="chip" style="font-size:0.7em;">${entry.modelIdentifier}</span>` : nothing}
                      ${entry.version ? html`<span class="chip" style="font-size:0.7em;">${entry.version}</span>` : nothing}
                    </div>
                  </div>
                  <div class="list-meta" style="font-size:0.75em;">
                    <div>${formatPresenceAge(entry)}</div>
                    <div class="muted">Last input ${lastInput}</div>
                    <div class="muted">Reason ${entry.reason ?? ""}</div>
                  </div>
                </div>
              `;
            })}
      </div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Main render
// ---------------------------------------------------------------------------

export function renderOverview(props: OverviewProps) {
  const lastRefresh = props.lastChannelsRefresh
    ? formatRelativeTimestamp(props.lastChannelsRefresh)
    : "n/a";

  const authHint = (() => {
    if (props.connected || !props.lastError) return null;
    const lower = props.lastError.toLowerCase();
    const authFailed = lower.includes("unauthorized") || lower.includes("connect failed");
    if (!authFailed) return null;
    const hasToken = Boolean(props.settings.token.trim());
    const hasPassword = Boolean(props.password.trim());
    if (!hasToken && !hasPassword) {
      return html`<div class="muted" style="margin-top:8px;">
        This gateway requires auth. Add a token or password, then click Connect.
      </div>`;
    }
    return html`<div class="muted" style="margin-top:8px;">Auth failed. Update token/password, then Connect.</div>`;
  })();

  return html`
    <div class="ops">
      <!-- Status bar -->
      <div class="ops-status">
        <span class="ops-status__pill ${props.connected ? "live" : "down"}">
          <span class="ops-beacon"></span>
          ${props.connected ? "Live" : "Offline"}
        </span>
        <div class="ops-status__field">
          <span class="k">Channels sync</span><span class="v">${lastRefresh}</span>
        </div>
        <div class="ops-status__field">
          <span class="k">Next cron</span><span class="v">${formatNextRun(props.cronNext)}</span>
        </div>
        <div class="ops-status__spacer"></div>
        <div class="ops-status__field">
          <span class="k">Range</span>
          <select
            class="btn"
            style="font-size:0.85em;padding:4px 8px;border-radius:6px;"
            .value=${props.overviewTimeRange}
            @change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement).value as TimeRange;
              props.onTimeRangeChange(v);
            }}
            ?disabled=${props.overviewLoading}
          >
            ${(TIME_RANGE_OPTIONS as Array<{ value: TimeRange; label: string }>).map(
              (opt) => html`<option value=${opt.value} ?selected=${opt.value === props.overviewTimeRange}>${opt.label}</option>`,
            )}
          </select>
        </div>
        <button class="btn" @click=${() => props.onRefresh()} ?disabled=${props.overviewLoading}>
          ${props.overviewLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      ${props.lastError
        ? html`<div class="callout danger"><div>${props.lastError}</div>${authHint ?? ""}</div>`
        : nothing}

      ${props.overviewError
        ? html`<div class="callout warning" style="margin:8px 0;">${props.overviewError}</div>`
        : nothing}

      <!-- Section 1: Consumption Dashboard -->
      ${renderConsumptionDashboard(props)}

      <!-- Section 2: Per-Agent Cards -->
      ${renderAgentCards(props)}

      <!-- Section: Connected Instances -->
      ${renderConnectedInstances(props)}

      <!-- Section 3: Provider Plans + System Health -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;">
        ${renderProviderPlans(props)}
        ${renderSystemHealth(props)}
      </div>

      <!-- Section 4: Collapsible Gateway Access + Maintenance -->
      ${renderGatewayAccess(props)}
    </div>
  `;
}
